'use client'

import { useCallback, useEffect, useRef } from 'react'
import Link from 'next/link'
import { Command } from 'cmdk'
import { HugeiconsIcon } from '@hugeicons/react'
import { SearchIcon, Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { useCommandSearchState } from '@/hooks/useCommandSearchState'
import { useCachedUser } from '@/hooks/useCachedUser'
import { CommandSearchResults } from '@/components/command-search/CommandSearchResults'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface CommandSearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpenChat?: (intent: string) => void
  onSelectThread?: (threadId: string) => void
}

export function CommandSearchDialog({
  open,
  onOpenChange,
  onOpenChat,
  onSelectThread,
}: CommandSearchDialogProps) {
  const { isLoggedIn } = useCachedUser()
  const {
    query,
    results,
    loading,
    handleValueChange,
    handleSelect,
    handleSelectThread,
    handleNewIntent,
    hasResults,
    showNav,
    reset,
  } = useCommandSearchState({ onOpenChat, onSelectThread, isLoggedIn })

  const containerRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    reset()
    onOpenChange(false)
  }, [onOpenChange, reset])

  const onSelectUrl = useCallback(
    (url: string) => {
      close()
      handleSelect(url)
    },
    [close, handleSelect]
  )

  const pickThread = useCallback(
    (threadId: string) => {
      close()
      handleSelectThread(threadId)
    },
    [close, handleSelectThread]
  )

  const onNewIntent = useCallback(
    (intent: string) => {
      close()
      handleNewIntent(intent)
    },
    [close, handleNewIntent]
  )

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset()
      onOpenChange(next)
    },
    [onOpenChange, reset]
  )

  useEffect(() => {
    if (!open) return
    const id = requestAnimationFrame(() => {
      const input = containerRef.current?.querySelector('[cmdk-input]') as HTMLInputElement | null
      input?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [open])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'k' || !(e.metaKey || e.ctrlKey)) return
      e.preventDefault()
      if (open) {
        close()
        return
      }
      onOpenChange(true)
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [open, close, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="left-0 top-0 h-dvh max-h-dvh max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-none border-0 bg-atrium-inner p-0 text-atrium-black sm:max-w-none lg:left-[50%] lg:top-[50%] lg:h-auto lg:max-h-[calc(100vh-4rem)] lg:max-w-xl lg:translate-x-[-50%] lg:translate-y-[-50%] lg:rounded-lg lg:border lg:border-atrium-border"
        showCloseButton={false}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
          <DialogDescription>
            Search campaigns, media, and conversations
          </DialogDescription>
        </DialogHeader>

        <div ref={containerRef} className="relative min-w-0">
          <Command shouldFilter={showNav} loop>
            {/*
              Search input row. Bigger on mobile (h-12 with safe-area-top) so
              the close affordance has room next to the input; smaller on desktop
              (h-11) where ⌘I is the primary close UX.
              Right-side controls:
                - Mobile (<lg): Cancel button. Big tap target, clearly labeled.
                - Desktop (lg+): ⌘I badge as a hint + a small X icon button.
                  We position the X below the badge area (top-2 right-2) so the
                  two never overlap.
            */}
            <div className="relative flex h-12 w-full shrink-0 items-center border-b border-atrium-border px-3 pt-[env(safe-area-inset-top)] lg:h-11 lg:pt-0">
              <HugeiconsIcon
                icon={SearchIcon}
                className="pointer-events-none absolute left-3 size-4 shrink-0 text-[#939294]"
              />
              <Command.Input
                value={query}
                onValueChange={handleValueChange}
                placeholder="Search anything..."
                className="min-h-0 w-full flex-1 rounded-none border-0 bg-transparent py-2 pl-9 pr-[72px] text-[13px] font-medium text-atrium-black outline-none placeholder:text-[#939294] focus-visible:ring-0 lg:pr-20"
              />
              {/* Mobile-only "Cancel" text button — always visible, easy target. */}
              <button
                type="button"
                onClick={close}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-sm font-medium text-accent-600 hover:bg-atrium-content active:bg-atrium-content lg:hidden"
              >
                Cancel
              </button>
              {/* Desktop-only ⌘I hint + X close, packed right-aligned with
                  proper spacing. Wrapping them in a flex row guarantees they
                  never collide regardless of whether either changes size. */}
              <div className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1.5 lg:flex">
                <kbd className="shrink-0 rounded border border-neutral-200 bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-[#939294]">
                  ⌘K
                </kbd>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close search"
                  className="pointer-events-auto inline-flex items-center justify-center rounded p-1 text-atrium-muted transition-colors hover:bg-atrium-content hover:text-atrium-black"
                >
                  <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
                </button>
              </div>
            </div>

            <Command.List className="h-[calc(100dvh-2.75rem)] overflow-y-auto p-1.5 lg:h-auto lg:max-h-[min(360px,calc(100vh-8rem))]">
              <CommandSearchResults
                query={query}
                results={results}
                loading={loading}
                showNav={showNav}
                hasResults={hasResults}
                onSelectUrl={onSelectUrl}
                onSelectThread={pickThread}
                onNewIntent={onNewIntent}
              />
            </Command.List>

            {!isLoggedIn ? (
              <div className="shrink-0 border-t border-atrium-border bg-atrium-background px-4 py-3 lg:hidden">
                <p className="mb-2.5 text-[12px] font-medium text-atrium-muted">
                  Sign in to install channels, run Commands, and save prompts.
                </p>
                <div className="flex gap-2">
                  <Link
                    href="/signup"
                    onClick={close}
                    className="flex flex-1 items-center justify-center rounded-xl bg-accent-600 px-4 py-2.5 text-[13px] font-bold text-white transition active:scale-[0.98]"
                  >
                    Create account
                  </Link>
                  <Link
                    href="/login"
                    onClick={close}
                    className="flex flex-1 items-center justify-center rounded-xl border border-atrium-border bg-atrium-content px-4 py-2.5 text-[13px] font-semibold text-atrium-black transition active:scale-[0.98]"
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            ) : null}
          </Command>
        </div>
      </DialogContent>
    </Dialog>
  )
}
